import utils from './stat-utils.js';
import StatEmitter from './stat-emitter.js';

// Default options for the plugin.
const DEFAULTS = {
	interval: 60,
	freqInterval: 1, // do not send if less than freq_interval time spent after previous send
	events: [],
	startBindEvents: [],	// for binding events
	startUnbindEvents: [],		// for unbinding events
	intervalStartEvents: [],
	intervalStopEvents: [],
	sendOnAd: true,
	sendImmediately: true
};

/**
 * The base Stat class to extend from. Sends nothing. Aggregates StatEmitter and utils methods.
 *
 * @param {Object} player   Main Player
 * @param {Object=} options Object of option names and values
 * @extends StatEmitter
 */

class StatSpy extends StatEmitter {
	constructor(player, options) {
		let options_ = Object.assign({}, DEFAULTS, options);
		super(player, options_);

		this.utils = {};
		this.interval = options_.interval;
		this.statEvents = new Set(options_.events);
		this.startBindEvents = options_.startBindEvents;
		this.startUnbindEvents = options_.startUnbindEvents;
		this.intervalStartEvents = options_.intervalStartEvents;
		this.intervalStopEvents = options_.intervalStopEvents;
		this.freqInterval = options_.freqInterval;
		this.sendOnAd = options_.sendOnAd;
		this.sendImmediately = options_.sendImmediately;
		this.lastSendTime = null;
		this.player = player;
		this.timer = null;

		for (let fn in utils) {
			this.utils[fn] = utils[fn];
		}

		if (this.startBindEvents.length || this.startUnbindEvents.length) {
			this._bindLimitingEvents();
		} else {
			this._bindEvents();
		}

		this.player.on('dispose', () => {
			this._unbindEvents();
		});
	}

	_bindEvents() {
		this._bindStaticEvents();
		this._bindIntervalEvents();
	}

	_unbindEvents(saveLimitingEvents) {
		this.statEvents.forEach((ev) => {
			this.off(ev);
		});
		this.intervalStartEvents.forEach((ev) => {
			this.off(ev);
		});
		if (!saveLimitingEvents) {
			this.startBindEvents.forEach((ev) => {
				this.off(ev);
			});
			this.startUnbindEvents.forEach((ev) => {
				this.off(ev);
			});
		}
	}

	_bindStaticEvents() {
		this.statEvents.forEach((ev) => {
			this.on(ev, (data) => {
				this.allowSend() && this.send(ev, data);
			});
		});
	}

	_bindIntervalEvents() {
		if (!this.interval) return;
		
		this.intervalStartEvents.forEach((ev) => {  
			this.on(ev, () => {
				this.sendImmediately && this.allowSend() && this.send(ev);
				if (!this.timer) {
					// Use an anonymous function for ability to change interval
					this.intervalFunction = () => { this.allowSend() && this.send(ev) }
					this.setInterval();
				}
			});
		});

		this.intervalStopEvents.forEach((ev) => {
			this.on(ev, () => {
				this.player.clearInterval(this.timer);
				this.timer = null;
			});
		});
	}

	_bindLimitingEvents() {
		let limitingEvents = this.startBindEvents.concat(this.startUnbindEvents);

		limitingEvents.forEach((ev) => {
			this.player.on(ev,  () => {
				this.trigger(ev);
			});
		});

		this.startBindEvents.forEach((ev) => {
			this.on(ev, () => {
				this._bindEvents();
			});
		});

		this.startUnbindEvents.forEach((ev) => {
			this.on(ev, () => {
				this._unbindEvents('saveLimitingEvents');
			});
		});
	}

	setInterval(newIntervalValue) {
		if (newIntervalValue) this.interval = newIntervalValue;
		if (this.timer) this.player.clearInterval(this.timer);
		this.timer = this.player.setInterval(this.intervalFunction.bind(this), this.interval * 1000);
	}

	_allowedOnAd() {
		return this.sendOnAd || !this.utils.isAdPlaying(this.player);
	}

	_allowedByFreq() {
		return (new Date()*1 - this.lastSendTime) / 1000 > this.freqInterval;
	}

	send(ev) {
		this.lastSendTime = new Date();
	}

	allowSend() {
		return this._allowedByFreq() && this._allowedOnAd();
	}

}

export default StatSpy;
